import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer
} from 'react';
import type { TableTrackingState, TrackingRegistryState } from './types';
import { dirtyTableIds } from './dirtyTableRegistry';

type TrackingAction =
  | { type: 'set-dirty'; tableId: string; dirtyCount: number }
  | { type: 'reset'; tableId?: string };

function reducer(state: Record<string, TableTrackingState>, action: TrackingAction) {
  switch (action.type) {
    case 'set-dirty': {
      const nextCount = Math.max(0, Number.isFinite(action.dirtyCount) ? action.dirtyCount : 0);
      if (nextCount > 0) dirtyTableIds.add(action.tableId);
      else dirtyTableIds.delete(action.tableId);
      const prev = state[action.tableId];
      if (prev?.dirtyCount === nextCount) {
        return state;
      }
      return {
        ...state,
        [action.tableId]: {
          dirtyCount: nextCount,
          lastChangedAt: Date.now()
        }
      };
    }
    case 'reset': {
      if (!action.tableId) {
        dirtyTableIds.clear();
        return {};
      }
      dirtyTableIds.delete(action.tableId);
      if (!state[action.tableId] || state[action.tableId].dirtyCount === 0) {
        return state;
      }
      return {
        ...state,
        [action.tableId]: {
          dirtyCount: 0,
          lastChangedAt: Date.now()
        }
      };
    }
    default:
      return state;
  }
}

const TrackingContext = createContext<TrackingRegistryState | null>(null);

export const TrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tables, dispatch] = useReducer(reducer, {});

  const setDirty = useCallback((tableId: string, dirtyCount: number) => {
    dispatch({ type: 'set-dirty', tableId, dirtyCount });
  }, []);

  const reset = useCallback((tableId?: string) => {
    dispatch({ type: 'reset', tableId });
  }, []);

  const getDirtyCount = useCallback(
    (tableId: string) => tables[tableId]?.dirtyCount ?? 0,
    [tables]
  );

  const totalDirty = useCallback(
    () => Object.values(tables).reduce((sum, entry) => sum + (entry.dirtyCount || 0), 0),
    [tables]
  );

  const value = useMemo<TrackingRegistryState>(() => ({
    tables,
    setDirty,
    reset,
    getDirtyCount,
    totalDirty
  }), [tables, setDirty, reset, getDirtyCount, totalDirty]);

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTrackingRegistry = (): TrackingRegistryState => {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTrackingRegistry must be used within a TrackingProvider');
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTrackingTable = (tableId: string) => {
  const registry = useTrackingRegistry();
  // registry.setDirty / registry.reset стабильны (useCallback([]) в провайдере),
  // тогда как сам объект registry меняется при каждом обновлении dirtyCount.
  // Зависим именно от методов — иначе setDirty/reset пересоздаются на каждый
  // ре-рендер любой таблицы, что каскадно дестабилизирует колбэки потребителей.
  const { setDirty: registrySetDirty, reset: registryReset } = registry;

  const setDirty = useCallback((dirtyCount: number) => {
    registrySetDirty(tableId, dirtyCount);
  }, [registrySetDirty, tableId]);

  const reset = useCallback(() => {
    registryReset(tableId);
  }, [registryReset, tableId]);

  const dirtyCount = registry.getDirtyCount(tableId);

  return {
    dirtyCount,
    isDirty: dirtyCount > 0,
    setDirty,
    reset,
    lastChangedAt: registry.tables[tableId]?.lastChangedAt
  };
};

export type { TableTrackingState, TrackingRegistryState };

